import { useState, useEffect, useMemo } from 'react'
import {
  Plus,
  Search,
  X,
  Check,
  ChevronDown,
  ChevronRight,
  Trash2,
  RefreshCw,
  Save,
  Utensils,
  Soup,
  AlertCircle,
  Filter,
  Sparkles,
  ChevronsUpDown,
  ArrowRightLeft
} from 'lucide-react'
import { useApp } from '../App'
import { menusAPI, dishesAPI, componentsAPI } from '../lib/api'
import { useToastActions } from '../components/Toast'
import ConfirmModal from '../components/ConfirmModal'

// ══════════════════════════════════════════════════════════════════════════════
// CATEGORY CONFIG
// ══════════════════════════════════════════════════════════════════════════════

const CATEGORY_CONFIG = {
  chicken: { color: 'bg-green-100 text-green-700 border-green-200', label: 'Chicken' },
  chicken_2: { color: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: 'Chicken 2' },
  beef: { color: 'bg-red-100 text-red-700 border-red-200', label: 'Beef' },
  seafood: { color: 'bg-cyan-100 text-cyan-700 border-cyan-200', label: 'Seafood' },
  seafood_2: { color: 'bg-teal-100 text-teal-700 border-teal-200', label: 'Seafood 2' },
  veg: { color: 'bg-lime-100 text-lime-700 border-lime-200', label: 'Veg' },
  chef: { color: 'bg-purple-100 text-purple-700 border-purple-200', label: 'Chef Special' },
  cheat: { color: 'bg-amber-100 text-amber-700 border-amber-200', label: 'Cheat Meal' },
  pasta: { color: 'bg-stone-100 text-stone-700 border-stone-200', label: 'Pasta' },
  salad: { color: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: 'Salad' },
  salad_2: { color: 'bg-emerald-50 text-emerald-600 border-emerald-200', label: 'Salad 2' },
  broth: { color: 'bg-orange-100 text-orange-700 border-orange-200', label: 'Broth' },
  broth_2: { color: 'bg-orange-50 text-orange-600 border-orange-200', label: 'Broth 2' },
  oats: { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', label: 'Oats' },
  chia: { color: 'bg-pink-100 text-pink-700 border-pink-200', label: 'Chia Pudding' },
  chia_2: { color: 'bg-pink-50 text-pink-600 border-pink-200', label: 'Chia 2' },
  wellness_shots: { color: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: 'Wellness Shots' },
  date_balls: { color: 'bg-amber-100 text-amber-800 border-amber-300', label: 'Date Balls' },
}

// Keep in sync with MealCounts.jsx / Plating.jsx / backend MEAL_CATEGORIES.
const SIDE_CATEGORIES = ['broth', 'broth_2', 'oats', 'chia', 'chia_2']
const ADDON_CATEGORIES = ['wellness', 'wellness_shots', 'date_balls']

// ══════════════════════════════════════════════════════════════════════════════
// DISH CARD (for selection)
// ══════════════════════════════════════════════════════════════════════════════

function DishSelectCard({ dish, selected, onToggle }) {
  const config = CATEGORY_CONFIG[dish.category] || CATEGORY_CONFIG.chicken

  return (
    <button
      onClick={() => onToggle(dish)}
      className={`text-left p-4 rounded-xl border-2 transition-all
        ${selected
          ? 'border-havn-green-600 bg-havn-green-50 shadow-soft'
          : 'border-havn-cream-300 bg-white hover:border-havn-green-300'}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <span className={`inline-block px-2 py-0.5 text-xs rounded-full border ${config.color} mb-2`}>
            {config.label}
          </span>
          <h4 className="font-semibold text-havn-charcoal truncate">{dish.name}</h4>
          {dish.cuisine && (
            <p className="text-xs text-havn-gray-500 mt-0.5">{dish.cuisine}</p>
          )}
        </div>
        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0
          ${selected
            ? 'bg-havn-green-600 border-havn-green-600 text-white'
            : 'border-havn-gray-300'}`}>
          {selected && <Check className="w-4 h-4" />}
        </div>
      </div>

      {/* Macros */}
      <div className="flex items-center gap-3 mt-3 text-xs text-havn-gray-500">
        <span>{dish.calories} cal</span>
        <span>{dish.protein}g protein</span>
      </div>
    </button>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPONENT TYPE LABELS
// ══════════════════════════════════════════════════════════════════════════════

const COMPONENT_TYPE_CONFIG = {
  protein: { label: 'Protein', color: 'bg-red-100 text-red-700' },
  vegetable: { label: 'Vegetable', color: 'bg-green-100 text-green-700' },
  carb: { label: 'Carb/Grain', color: 'bg-amber-100 text-amber-700' },
  sauce: { label: 'Sauce', color: 'bg-purple-100 text-purple-700' },
  base: { label: 'Base', color: 'bg-blue-100 text-blue-700' },
}

// ══════════════════════════════════════════════════════════════════════════════
// MENU DISH CARD (selected dish on menu)
// ══════════════════════════════════════════════════════════════════════════════

function MenuDishCard({ dish, onRemove, onSwapComponent, forceExpanded = null }) {
  const [localExpanded, setLocalExpanded] = useState(false)
  // forceExpanded: null = use local state, true = force expanded, false = force collapsed
  const expanded = forceExpanded !== null ? forceExpanded : localExpanded
  const config = CATEGORY_CONFIG[dish.category] || CATEGORY_CONFIG.chicken
  const isSide = SIDE_CATEGORIES.includes(dish.category) || ADDON_CATEGORIES.includes(dish.category)

  return (
    <div className="bg-white rounded-xl border border-havn-cream-300 overflow-hidden">
      <div className="p-4 flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`inline-block px-2 py-0.5 text-xs rounded-full border ${config.color}`}>
              {config.label}
            </span>
            {!isSide && dish.cuisine && (
              <span className="text-xs text-havn-gray-400">{dish.cuisine}</span>
            )}
          </div>
          <h4 className="font-display text-lg font-semibold text-havn-charcoal">{dish.name}</h4>

          {/* Macros */}
          <div className="flex items-center gap-4 mt-2 text-xs text-havn-gray-500">
            <span>{dish.calories} cal</span>
            <span>{dish.protein}g P</span>
            <span>{dish.fat}g F</span>
            <span>{dish.carbs}g C</span>
          </div>
        </div>

        <div className="flex items-center gap-1">
          {!isSide && dish.components?.length > 0 && (
            <button
              onClick={() => setLocalExpanded(!localExpanded)}
              className={`p-2 rounded-lg transition-colors ${
                expanded
                  ? 'text-havn-green-700 bg-havn-green-50 hover:bg-havn-green-100'
                  : 'text-havn-gray-400 hover:text-havn-charcoal hover:bg-havn-cream-100'
              }`}
              title={expanded ? 'Collapse components' : 'Expand components'}
            >
              {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          )}
          <button
            onClick={() => onRemove(dish)}
            className="p-2 text-havn-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            title="Remove from menu"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Components (expandable) */}
      {expanded && dish.components?.length > 0 && (
        <div className="px-4 pb-4 pt-2 border-t border-havn-cream-200 bg-havn-cream-50">
          <div className="text-xs font-medium text-havn-gray-500 uppercase tracking-wide mb-2">
            Components — Click swap to customize
          </div>
          <div className="space-y-2">
            {dish.components.map((comp, i) => {
              const compConfig = COMPONENT_TYPE_CONFIG[comp.component_type] || COMPONENT_TYPE_CONFIG.protein
              // Check if this component is swappable (protein, vegetables, carbs, sauces)
              const isSwappable = ['protein', 'vegetable', 'carb', 'sauce'].includes(comp.component_type)
              const displayName = comp.name

              return (
                <div
                  key={i}
                  className={`flex items-center justify-between py-2 px-3 rounded-lg transition-colors ${
                    isSwappable ? 'bg-white hover:bg-havn-green-50 cursor-pointer' : 'bg-white/50'
                  }`}
                  onClick={isSwappable ? () => onSwapComponent(dish, comp) : undefined}
                >
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-0.5 text-xs rounded-full ${compConfig.color}`}>
                      {compConfig.label}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-medium text-havn-charcoal">{displayName}</p>
                      {comp.is_swapped && (
                        <span className="text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded" title={`Original: ${comp.original_name}`}>
                          swapped
                        </span>
                      )}
                    </div>
                  </div>
                  {isSwappable && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onSwapComponent(dish, comp) }}
                      className="flex items-center gap-1 text-xs text-havn-green-700 hover:text-havn-green-800 font-medium px-2 py-1 rounded hover:bg-havn-green-100 transition-colors"
                    >
                      <ArrowRightLeft className="w-3 h-3" />
                      Swap
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// DISH SELECTION MODAL
// ══════════════════════════════════════════════════════════════════════════════

function DishSelectionModal({ dishes, selectedDishes, onAdd, onClose }) {
  const [searchTerm, setSearchTerm] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  // Build initial set from both id and dish_id to handle dishes from menu (dish_id) and library (id)
  const [localSelected, setLocalSelected] = useState(new Set(selectedDishes.map(d => d.id || d.dish_id)))

  const categories = [
    'chicken', 'chicken_2', 'beef', 'seafood', 'seafood_2', 'veg', 'cheat', 'pasta', 'salad', 'salad_2',
    'broth', 'broth_2', 'oats', 'chia', 'chia_2',
    'wellness_shots', 'date_balls',
  ]

  const filteredDishes = useMemo(() => {
    return dishes.filter(dish => {
      const matchesSearch = !searchTerm ||
        dish.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        dish.cuisine?.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesCategory = !categoryFilter || dish.category === categoryFilter
      return matchesSearch && matchesCategory
    })
  }, [dishes, searchTerm, categoryFilter])

  function toggleDish(dish) {
    const newSelected = new Set(localSelected)
    if (newSelected.has(dish.id)) {
      newSelected.delete(dish.id)
    } else {
      newSelected.add(dish.id)
    }
    setLocalSelected(newSelected)
  }

  function handleSave() {
    const dishesToAdd = dishes.filter(d => localSelected.has(d.id) && !selectedDishes.some(sd => sd.id === d.id || sd.dish_id === d.id))
    onAdd(dishesToAdd)
    onClose()
  }

  const newSelections = [...localSelected].filter(id => !selectedDishes.some(d => d.id === id || d.dish_id === id)).length

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="modal-header">
          <div>
            <h3 className="font-display text-xl font-semibold text-havn-green-900">
              Add Dishes to Menu
            </h3>
            <p className="text-sm text-havn-gray-500 mt-1">
              {localSelected.size} selected • {newSelections} new
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-havn-gray-400 hover:text-havn-charcoal rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filters */}
        <div className="px-6 py-4 border-b border-havn-cream-200 flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-havn-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search dishes..."
              className="input pl-10"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="input w-40"
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{CATEGORY_CONFIG[cat]?.label || cat}</option>
            ))}
          </select>
        </div>

        {/* Dish Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredDishes.map(dish => (
              <DishSelectCard
                key={dish.id}
                dish={dish}
                selected={localSelected.has(dish.id)}
                onToggle={toggleDish}
              />
            ))}
          </div>
          {filteredDishes.length === 0 && (
            <div className="text-center py-12 text-havn-gray-500">
              No dishes found matching your filters
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={handleSave} className="btn-primary" disabled={newSelections === 0}>
            <Plus className="w-4 h-4" />
            Add {newSelections} Dish{newSelections !== 1 ? 'es' : ''}
          </button>
        </div>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPONENT SWAP MODAL
// ══════════════════════════════════════════════════════════════════════════════

function ComponentSwapModal({ dish, component, swapOptions, onSwap, onClose }) {
  const [selected, setSelected] = useState(component.name)
  const [searchTerm, setSearchTerm] = useState('')

  // Map component_type to swap pool key
  const swapPoolKey = {
    'protein': 'proteins',
    'vegetable': 'vegetables',
    'carb': 'grains',
    'sauce': 'sauces',
  }[component.component_type] || component.component_type

  // Get options from swap pools
  const allOptions = swapOptions[swapPoolKey] || []

  // Filter options by search
  const options = useMemo(() => {
    if (!searchTerm) return allOptions
    return allOptions.filter(opt =>
      opt.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [allOptions, searchTerm])

  const compConfig = COMPONENT_TYPE_CONFIG[component.component_type] || COMPONENT_TYPE_CONFIG.protein

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal max-w-md">
        <div className="modal-header">
          <div>
            <h3 className="font-display text-lg font-semibold text-havn-green-900">
              <span className={`inline-block px-2 py-0.5 text-xs rounded-full mr-2 ${compConfig.color}`}>
                {compConfig.label}
              </span>
              Swap
            </h3>
            <p className="text-sm text-havn-gray-500 mt-1">
              Currently: <strong>{component.name}</strong> in {dish.name}
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-havn-gray-400 hover:text-havn-charcoal rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="modal-body">
          {/* Search */}
          {allOptions.length > 6 && (
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-havn-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={`Search ${swapPoolKey}...`}
                className="input pl-10"
              />
            </div>
          )}

          <div className="space-y-2 max-h-80 overflow-y-auto">
            {options.length === 0 ? (
              <p className="text-center text-havn-gray-500 py-4">
                No {swapPoolKey} found
              </p>
            ) : (
              options.map((opt, i) => {
                const isSelected = selected === opt.name
                const isCurrent = component.name === opt.name

                return (
                  <button
                    key={i}
                    onClick={() => setSelected(opt.name)}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-all
                      ${isSelected
                        ? 'border-havn-green-600 bg-havn-green-50'
                        : 'border-havn-cream-200 hover:border-havn-green-300'}
                      ${isCurrent ? 'ring-2 ring-purple-200' : ''}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-havn-charcoal">{opt.name}</p>
                          {isCurrent && (
                            <span className="text-xs bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded">
                              Current
                            </span>
                          )}
                        </div>
                        {/* Show macros if available */}
                        <p className="text-xs text-havn-gray-500 mt-0.5">
                          {opt.cal || opt.calories} cal • {opt.p || opt.protein}g P • {opt.f || opt.fat}g F • {opt.c || opt.carbs}g C
                        </p>
                      </div>
                      {isSelected && (
                        <Check className="w-5 h-5 text-havn-green-600 flex-shrink-0" />
                      )}
                    </div>
                  </button>
                )
              })
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button
            onClick={() => { onSwap(dish, component, selected); onClose() }}
            className="btn-primary"
            disabled={selected === component.name}
          >
            <ArrowRightLeft className="w-4 h-4" />
            Swap to {selected}
          </button>
        </div>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════════════════════════

export default function MenuBuilder() {
  const { currentMenu, currentCity } = useApp()
  const [menu, setMenu] = useState(null)
  const [allDishes, setAllDishes] = useState([])
  const [swapPools, setSwapPools] = useState({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [swapModal, setSwapModal] = useState(null)
  const [removeDishConfirm, setRemoveDishConfirm] = useState(null) // dish to confirm removal
  const [expandAll, setExpandAll] = useState(null)  // null = local state, true = force expanded, false = force collapsed
  const { success, error } = useToastActions()

  // Load data
  useEffect(() => {
    async function loadData() {
      if (!currentMenu) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const [menuData, dishesData, swapData] = await Promise.all([
          menusAPI.get(currentMenu),
          dishesAPI.list({ city_id: currentCity }),
          componentsAPI.getSwapPools().catch(() => ({})),
        ])
        setMenu(menuData)
        setAllDishes(dishesData)
        setSwapPools(swapData)
      } catch (err) {
        error('Failed to load menu data')
        console.error(err)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [currentMenu])

  // Add dishes to menu
  async function handleAddDishes(dishes) {
    try {
      setSaving(true)
      for (const dish of dishes) {
        await menusAPI.addDish(currentMenu, dish.id, 0)
      }
      // Reload menu
      const menuData = await menusAPI.get(currentMenu)
      setMenu(menuData)
      success(`Added ${dishes.length} dish${dishes.length !== 1 ? 'es' : ''} to menu`)
    } catch (err) {
      error('Failed to add dishes')
    } finally {
      setSaving(false)
    }
  }

  // Remove dish from menu — shows ConfirmModal instead of native confirm()
  function handleRemoveDish(dish) {
    setRemoveDishConfirm(dish)
  }

  async function confirmRemoveDish() {
    const dish = removeDishConfirm
    if (!dish) return
    setRemoveDishConfirm(null)

    try {
      await menusAPI.removeDish(currentMenu, dish.dish_id)
      const menuData = await menusAPI.get(currentMenu)
      setMenu(menuData)
      success('Dish removed')
    } catch (err) {
      error('Failed to remove dish')
    }
  }

  // Swap component
  function handleSwapComponent(dish, component) {
    setSwapModal({ dish, component })
  }

  async function handleSwap(dish, component, newComponentName) {
    if (component.name === newComponentName) return

    try {
      // Build updated customizations JSON
      const existingCust = dish.customizations ? JSON.parse(dish.customizations) : { swaps: {} }
      const swaps = { ...(existingCust.swaps || {}) }

      // Find this component's type index (for dishes with 2 vegetables, etc.)
      const sameType = (dish.components || []).filter(c => c.component_type === component.component_type)
      const typeIdx = sameType.findIndex(c => c.name === component.name)
      const swapKey = `${component.component_type}_${typeIdx >= 0 ? typeIdx : 0}`

      // Determine original name (might already have been swapped)
      const originalName = component.original_name || component.name

      if (newComponentName === originalName) {
        // Swapping back to original — remove the customization
        delete swaps[swapKey]
      } else {
        swaps[swapKey] = { original: originalName, new: newComponentName }
      }

      const customizations = Object.keys(swaps).length > 0
        ? JSON.stringify({ swaps })
        : null

      // Persist to backend
      await menusAPI.updateDishCustomizations(currentMenu, dish.dish_id, customizations)

      // Reload menu to reflect saved swap
      const menuData = await menusAPI.get(currentMenu)
      setMenu(menuData)

      success(`Swapped ${component.name} → ${newComponentName}`)
    } catch (err) {
      console.error('Swap failed:', err)
      error('Failed to save swap: ' + (err.message || 'Unknown error'))
    }
  }

  // Group dishes by type
  const { meals, sides } = useMemo(() => {
    if (!menu?.dishes) return { meals: [], sides: [] }
    return {
      meals: menu.dishes.filter(d => !SIDE_CATEGORIES.includes(d.category) && !ADDON_CATEGORIES.includes(d.category)),
      sides: menu.dishes.filter(d => SIDE_CATEGORIES.includes(d.category) || ADDON_CATEGORIES.includes(d.category)),
    }
  }, [menu])

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8 text-havn-green-700" />
      </div>
    )
  }

  // No menu selected
  if (!currentMenu) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <AlertCircle className="w-12 h-12 text-havn-gray-300 mb-4" />
        <h3 className="font-display text-lg font-semibold text-havn-gray-500">No Menu Selected</h3>
        <p className="text-sm text-havn-gray-400 mt-1">
          Select or create a menu from the sidebar to start building
        </p>
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-xl font-semibold text-havn-green-900">
            {menu?.name || 'Menu'}
          </h2>
          <p className="text-sm text-havn-gray-500 mt-1">
            {meals.length} meals • {sides.length} sides
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setExpandAll(expandAll === true ? false : true)}
            className={`btn-secondary ${expandAll === true ? 'bg-havn-green-100 border-havn-green-300 text-havn-green-700' : ''}`}
            title={expandAll === true ? 'Collapse all components' : 'Expand all components'}
          >
            <ChevronsUpDown className="w-4 h-4" />
            {expandAll === true ? 'Collapse All' : 'Expand All'}
          </button>
          <button onClick={() => setShowAddModal(true)} className="btn-primary">
            <Plus className="w-4 h-4" />
            Add Dishes
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="bg-white rounded-xl border border-havn-cream-300 p-4">
          <div className="flex items-center gap-2 text-havn-gray-500 mb-1">
            <Utensils className="w-4 h-4" />
            <span className="text-xs uppercase tracking-wide">Meals</span>
          </div>
          <div className="text-2xl font-display font-semibold text-havn-green-900">{meals.length}</div>
        </div>
        <div className="bg-white rounded-xl border border-havn-cream-300 p-4">
          <div className="flex items-center gap-2 text-havn-gray-500 mb-1">
            <Soup className="w-4 h-4" />
            <span className="text-xs uppercase tracking-wide">Sides</span>
          </div>
          <div className="text-2xl font-display font-semibold text-havn-green-900">{sides.length}</div>
        </div>
        <div className="bg-white rounded-xl border border-havn-cream-300 p-4">
          <div className="text-xs text-havn-gray-500 uppercase tracking-wide mb-1">Avg Calories</div>
          <div className="text-2xl font-display font-semibold text-havn-green-900">
            {meals.length > 0 ? Math.round(meals.reduce((s, d) => s + (d.calories || 0), 0) / meals.length) : 0}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-havn-cream-300 p-4">
          <div className="text-xs text-havn-gray-500 uppercase tracking-wide mb-1">Avg Protein</div>
          <div className="text-2xl font-display font-semibold text-havn-green-900">
            {meals.length > 0 ? Math.round(meals.reduce((s, d) => s + (d.protein || 0), 0) / meals.length) : 0}g
          </div>
        </div>
      </div>

      {/* Meals Section */}
      <div className="mb-8">
        <h3 className="font-display text-lg font-semibold text-havn-green-900 mb-4">
          Meals
        </h3>
        {meals.length === 0 ? (
          <div className="bg-havn-cream-50 rounded-xl border border-dashed border-havn-cream-400 p-8 text-center">
            <Utensils className="w-10 h-10 text-havn-gray-300 mx-auto mb-3" />
            <p className="text-havn-gray-500">No meals added yet</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="mt-3 text-sm text-havn-green-700 hover:text-havn-green-800 font-medium"
            >
              + Add your first meal
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {meals.map(dish => (
              <MenuDishCard
                key={dish.dish_id}
                dish={dish}
                onRemove={handleRemoveDish}
                onSwapComponent={handleSwapComponent}
                forceExpanded={expandAll}
              />
            ))}
          </div>
        )}
      </div>

      {/* Sides Section */}
      <div>
        <h3 className="font-display text-lg font-semibold text-havn-green-900 mb-4">
          Sides
        </h3>
        {sides.length === 0 ? (
          <div className="bg-havn-cream-50 rounded-xl border border-dashed border-havn-cream-400 p-8 text-center">
            <Soup className="w-10 h-10 text-havn-gray-300 mx-auto mb-3" />
            <p className="text-havn-gray-500">No sides added yet</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="mt-3 text-sm text-havn-green-700 hover:text-havn-green-800 font-medium"
            >
              + Add broths or chia puddings
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {sides.map(dish => (
              <MenuDishCard
                key={dish.dish_id}
                dish={dish}
                onRemove={handleRemoveDish}
                onSwapComponent={handleSwapComponent}
                forceExpanded={expandAll}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add Dishes Modal */}
      {showAddModal && (
        <DishSelectionModal
          dishes={allDishes}
          selectedDishes={menu?.dishes || []}
          onAdd={handleAddDishes}
          onClose={() => setShowAddModal(false)}
        />
      )}

      {/* Swap Modal */}
      {swapModal && (
        <ComponentSwapModal
          dish={swapModal.dish}
          component={swapModal.component}
          swapOptions={swapPools}
          onSwap={handleSwap}
          onClose={() => setSwapModal(null)}
        />
      )}

      {/* Remove Dish Confirmation */}
      <ConfirmModal
        isOpen={!!removeDishConfirm}
        onClose={() => setRemoveDishConfirm(null)}
        onConfirm={confirmRemoveDish}
        title="Remove Dish"
        message={`Remove "${removeDishConfirm?.name}" from this menu?`}
        confirmText="Remove"
        variant="danger"
      />
    </div>
  )
}
